// The board's shore data (edges, field, ramp layout), computed once per terrain signature and shared by every
// consumer (water shader, ramp mesh, rocks, open-sea ring). The snapshot's tile array is fresh on every publish
// (renderer-crash-rca #139/#140), so everything here is keyed on a content signature, never on that array.

import type { SceneNode } from '@aod/protocol';
import * as THREE from 'three';
import { buildBiomeMap } from '../elevation.js';
import { type ShoreEdge, collectShoreEdges } from './shoreEdges.js';
import { DIST_MAX, type ShoreField, buildShoreField, shoreFieldTexture } from './shoreField.js';
import { type RampLayout, buildRampLayout } from './shoreRamp.js';

export interface ShoreBoard {
  readonly signature: string;
  readonly biomeMap: Map<string, string>;
  readonly edges: ReadonlyArray<ShoreEdge>;
  readonly field: ShoreField;
  readonly ramp: RampLayout;
}

/** Everything that changes what the shore looks like: tile identity, biome, level, river edges, position. */
export function shoreSignature(tiles: ReadonlyArray<SceneNode>): string {
  let h = 2166136261;
  let n = 0;
  const mix = (v: number) => {
    h = Math.imul(h ^ (v | 0), 16777619);
  };
  for (const t of tiles) {
    const p = (t.props ?? {}) as Record<string, unknown>;
    if (p.q === undefined || p.r === undefined) continue;
    n++;
    mix((p.q as number) + 1000);
    mix((p.r as number) + 1000);
    const b = (p.biome as string | undefined) ?? '';
    for (let i = 0; i < b.length; i++) mix(b.charCodeAt(i));
    mix(((p.level as number | undefined) ?? -1) + 7);
    mix(Number(p.riverEdges ?? 0) + 3);
  }
  return `${n}:${(h >>> 0).toString(36)}`;
}

let cached: ShoreBoard | null = null;

/** Shore data for `tiles` (cached by signature; a different board replaces the single cached entry). */
export function shoreBoardFor(tiles: ReadonlyArray<SceneNode>): ShoreBoard {
  const signature = shoreSignature(tiles);
  if (cached && cached.signature === signature) return cached;
  const biomeMap = buildBiomeMap(tiles);
  const edges = collectShoreEdges(tiles, biomeMap);
  const flat: Array<{ q: number; r: number; x: number; z: number; biome: string }> = [];
  for (const t of tiles) {
    const p = (t.props ?? {}) as Record<string, unknown>;
    if (p.q === undefined || p.r === undefined) continue;
    const [x, , z] = t.transform.position;
    flat.push({
      q: p.q as number,
      r: p.r as number,
      x,
      z,
      biome: (p.biome as string | undefined) ?? 'plains',
    });
  }
  cached = {
    signature,
    biomeMap,
    edges,
    field: buildShoreField(flat, edges),
    ramp: buildRampLayout(edges),
  };
  return cached;
}

/** Clear the cache (tests). */
export function resetShoreBoard(): void {
  cached = null;
}

// ── GPU side: the uniforms every water material shares ─────────────────────────────────────────────────────
const blank = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat);
blank.needsUpdate = true;

/** Shared by every sea water material (and read by TerrainWater's shader): the field texture + its mapping. */
export const shoreUniforms = {
  uShoreTex: { value: blank as THREE.Texture },
  uShoreOrg: { value: new THREE.Vector2(0, 0) },
  uShoreInv: { value: new THREE.Vector2(1, 1) },
};

let uploaded: ShoreField | null = null;
let uploadedTex: THREE.DataTexture | null = null;

/** Point the shared uniforms at `field` (no-op if it is already the uploaded one). */
export function setShoreField(field: ShoreField): void {
  if (uploaded === field) return;
  uploadedTex?.dispose();
  uploadedTex = shoreFieldTexture(field);
  uploaded = field;
  shoreUniforms.uShoreTex.value = uploadedTex;
  shoreUniforms.uShoreOrg.value.set(field.ox, field.oz);
  shoreUniforms.uShoreInv.value.set(
    1 / (field.width * field.cell),
    1 / (field.height * field.cell),
  );
}

export { DIST_MAX };
